const RECAPTCHA_SCRIPT_ID = "google-recaptcha-v2-script";

type Recaptcha = {
    ready: (callback: () => void) => void;
    render: (container: HTMLElement, parameters: { sitekey: string; size: "invisible"; callback: (token: string) => void }) => number;
    execute: (widgetId: number) => void;
    reset: (widgetId: number) => void;
};

declare global {
    interface Window {
        grecaptcha?: Recaptcha;
    }
}

export const recaptchaConfiguration = {
    siteKey: "",
};

let scriptLoadingPromise: Promise<void> | null = null;

type CaptchaWidgetState = {
    widgetId: number;
    pendingResolve: ((result: { token: string; widgetId: number }) => void) | null;
};

const widgetStateByContainer = new WeakMap<HTMLElement, CaptchaWidgetState>();

const isGrecaptchaReady = () =>
    typeof window.grecaptcha?.render === "function" && typeof window.grecaptcha?.execute === "function";

const waitForGrecaptchaReady = (): Promise<void> => {
    if (isGrecaptchaReady()) {
        return Promise.resolve();
    }

    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) {
        return Promise.reject(new Error("captcha_load_error"));
    }

    return new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
            if (isGrecaptchaReady()) {
                resolve();
                return;
            }

            reject(new Error("captcha_load_error"));
        });
    });
};

export const loadRecaptchaConfiguration = () => {
    const vueConfig = document.querySelector("#vue-config");
    recaptchaConfiguration.siteKey = vueConfig?.getAttribute("data-google-captcha-site-key") ?? "";
};

export const loadRecaptchaScript = () => {
    if (isGrecaptchaReady()) {
        return Promise.resolve();
    }

    if (scriptLoadingPromise) {
        return scriptLoadingPromise;
    }

    scriptLoadingPromise = new Promise<void>((resolve, reject) => {
        const fail = () => {
            scriptLoadingPromise = null;
            document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
            reject(new Error("captcha_load_error"));
        };

        const succeed = () => {
            waitForGrecaptchaReady().then(resolve).catch(fail);
        };

        const existingScript = document.querySelector<HTMLScriptElement>("script[src^='https://www.google.com/recaptcha/api.js']");
        if (existingScript) {
            if (window.grecaptcha) {
                void waitForGrecaptchaReady().then(resolve).catch(fail);
                return;
            }

            existingScript.addEventListener("load", succeed, { once: true });
            existingScript.addEventListener("error", fail, { once: true });
            return;
        }

        const script = document.createElement("script");
        script.id = RECAPTCHA_SCRIPT_ID;
        script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.addEventListener("load", succeed, { once: true });
        script.addEventListener("error", fail, { once: true });
        document.head.appendChild(script);
    }).catch((error) => {
        scriptLoadingPromise = null;
        throw error;
    });

    return scriptLoadingPromise;
};

export const executeInvisibleRecaptcha = async (container: HTMLElement) =>
    new Promise<{ token: string; widgetId: number }>((resolve, reject) => {
        if (!recaptchaConfiguration.siteKey) {
            reject(new Error("captcha_configuration_error"));
            return;
        }

        loadRecaptchaScript()
            .then(() => {
                const grecaptcha = window.grecaptcha;
                if (!grecaptcha) {
                    reject(new Error("captcha_configuration_error"));
                    return;
                }

                grecaptcha.ready(() => {
                    let state = widgetStateByContainer.get(container);

                    if (!state) {
                        const newWidgetId = grecaptcha.render(container, {
                            sitekey: recaptchaConfiguration.siteKey,
                            size: "invisible",
                            callback: (token: string) => {
                                const currentState = widgetStateByContainer.get(container);
                                if (!currentState?.pendingResolve) {
                                    return;
                                }

                                currentState.pendingResolve({
                                    token,
                                    widgetId: currentState.widgetId,
                                });
                                currentState.pendingResolve = null;
                            },
                        });

                        state = { widgetId: newWidgetId, pendingResolve: null };
                        widgetStateByContainer.set(container, state);
                    }

                    state.pendingResolve = resolve;
                    grecaptcha.execute(state.widgetId);
                });
            })
            .catch(reject);
    });

export const resetRecaptcha = (container: HTMLElement | null) => {
    if (!container) {
        return;
    }

    const state = widgetStateByContainer.get(container);
    if (state && window.grecaptcha) {
        window.grecaptcha.reset(state.widgetId);
    }
};
